import React, { useMemo, useState } from 'react';
import { Table, Paper, Tabs, Badge, Tooltip, Text, ActionIcon, Group, Notification, TextInput } from '@mantine/core';
import { IconDeviceFloppy, IconPencil, IconTrash, IconX } from '@tabler/icons-react';
import { ActionButton } from '../components/NavigationButtons';
import AIGeneratingIcon from '../components/AIGeneratingIcon';

export default function ScenarioActionPlan({ modelId, assessmentUuid, scenarioId, controlsByScenario = {}, onSaveControl, onRefresh, hideHeader = false, isGenerating = false }) {
  const [activeTab, setActiveTab] = useState('generated');
  const [warningMessage, setWarningMessage] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const currentControls = useMemo(() => controlsByScenario[scenarioId] || [], [controlsByScenario, scenarioId]);

  const countByStatus = useMemo(() => {
    const res = { generated: 0, saved: 0, discarded: 0 };
    (currentControls || []).forEach((c) => {
      const s = (c.status || 'generated').toLowerCase();
      if (s === 'updated') res.saved += 1; // treat updated as saved
      else if (res[s] !== undefined) res[s] += 1;
    });
    return res;
  }, [currentControls]);

  const controlsForActive = useMemo(() => {
    const list = currentControls || [];
    if (activeTab === 'saved') {
      return list.filter((c) => ['saved', 'updated'].includes((c.status || '').toLowerCase()));
    }
    return list.filter((c) => (c.status || 'generated').toLowerCase() === activeTab);
  }, [currentControls, activeTab]);

  return (
    <Paper withBorder radius="md" p="md">
      {!hideHeader && (
        <Group justify="space-between" mb="sm">
          <Text fw={600}>Mitigating actions</Text>
          <Group gap="xs">
            {currentControls.length > 0 ? (
              <ActionButton
                leftSection={<AIGeneratingIcon isGenerating={isGenerating} />}
                onClick={() => onRefresh?.(scenarioId)}
              >
                Generate action plan
              </ActionButton>
            ) : null}
          </Group>
        </Group>
      )}

      {hideHeader && currentControls.length > 0 && (
        <Group justify="flex-end" mb="sm">
          <ActionButton leftSection={<AIGeneratingIcon isGenerating={isGenerating} />} onClick={() => onRefresh?.(scenarioId)}>
            Generate action plan
          </ActionButton>
        </Group>
      )}

      {currentControls.length === 0 ? (
        <Group justify="center" align="center" style={{ minHeight: 120 }}>
          <Text c="dimmed">No mitigating actions yet.</Text>
          <ActionButton leftSection={<AIGeneratingIcon isGenerating={isGenerating} />} ml={8} onClick={() => onRefresh?.(scenarioId)}>
            Generate action plan
          </ActionButton>
        </Group>
      ) : (
      <Tabs value={activeTab} onChange={setActiveTab} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="generated">
            <Group gap={6}>
              <span>Generated</span>
              <Badge variant="light" color="blue" radius="sm">{countByStatus.generated}</Badge>
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="saved">
            <Group gap={6}>
              <span>Saved</span>
              <Badge variant="light" color="green" radius="sm">{countByStatus.saved}</Badge>
            </Group>
          </Tabs.Tab>
          <Tabs.Tab value="discarded">
            <Group gap={6}>
              <span>Discarded</span>
              <Badge variant="light" color="gray" radius="sm">{countByStatus.discarded}</Badge>
            </Group>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="generated" pt="sm">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Mitigating action</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {controlsForActive.map((control) => (
                <Table.Tr key={control.controlId || control.name}>
                  <Table.Td>{control.name}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Tooltip label={!scenarioId ? 'Save the scenario first' : 'Save Control'}>
                      <ActionIcon
                        variant="light"
                        onClick={() => onSaveControl?.(control, scenarioId)}
                        disabled={!scenarioId}
                      >
                        <IconDeviceFloppy size={18} />
                      </ActionIcon>
                    </Tooltip>
                    <ActionIcon variant="light" disabled={!scenarioId} ml={6}>
                      <IconPencil size={18} />
                    </ActionIcon>
                    <ActionIcon variant="light" disabled={!scenarioId} ml={6}>
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Tabs.Panel>

        <Tabs.Panel value="saved" pt="sm">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Mitigating action</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {controlsForActive.map((control) => (
                <Table.Tr key={control.controlId || control.name}>
                  <Table.Td>
                    {editingId === (control.controlId || control.name) ? (
                      <Group gap={6} justify="flex-start">
                        <TextInput size="sm" value={editName} onChange={(e) => setEditName(e.currentTarget.value)} />
                        <ActionIcon
                          variant="light"
                          onClick={() => {
                            const payload = { ...control, name: editName };
                            onSaveControl?.(payload, scenarioId);
                            setEditingId(null);
                            setEditName('');
                          }}
                          disabled={!scenarioId || !editName.trim()}
                        >
                          <IconDeviceFloppy size={18} />
                        </ActionIcon>
                        <ActionIcon variant="light" color="red" onClick={() => { setEditingId(null); setEditName(''); }}>
                          <IconX size={18} />
                        </ActionIcon>
                      </Group>
                    ) : (
                      control.name
                    )}
                  </Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    {editingId !== (control.controlId || control.name) && (
                      <ActionIcon
                        variant="light"
                        disabled={!scenarioId}
                        ml={6}
                        onClick={() => {
                          setEditingId(control.controlId || control.name);
                          setEditName(control.name || '');
                        }}
                      >
                        <IconPencil size={18} />
                      </ActionIcon>
                    )}
                    <ActionIcon variant="light" disabled={!scenarioId} ml={6}>
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Tabs.Panel>

        <Tabs.Panel value="discarded" pt="sm">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Mitigating action</Table.Th>
                <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {controlsForActive.map((control) => (
                <Table.Tr key={control.controlId || control.name}>
                  <Table.Td>{control.name}</Table.Td>
                  <Table.Td style={{ textAlign: 'right' }}>
                    <ActionIcon variant="light" disabled={!scenarioId} ml={6}>
                      <IconPencil size={18} />
                    </ActionIcon>
                    <ActionIcon variant="light" disabled={!scenarioId} ml={6}>
                      <IconTrash size={18} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Tabs.Panel>
      </Tabs>
      )}

      {Boolean(warningMessage) && (
        <div style={{ position: 'fixed', bottom: 16, right: 16, zIndex: 9999 }}>
          <Notification
            withBorder
            color="yellow"
            withCloseButton
            onClose={() => setWarningMessage('')}
          >
            {warningMessage}
          </Notification>
        </div>
      )}
    </Paper>
  );
}
